import {catchError, first, map, mergeMap} from 'rxjs/operators';
import {Component, NgZone, OnDestroy} from '@angular/core';
import {BehaviorSubject, EMPTY, Subject} from 'rxjs';
import {ScreenshotConfig} from './models/screenshot-config';
import {CommonModule} from '@angular/common';
import {PreviewImageComponent} from '@q9elements/ui-kit/components';
import {ConfigService, SCREENSHOT_SIZE} from '@q9elements/ui-kit/common';
import {ScreenshotDataCell, ScreenshotService} from 'src/app/core/services/screenshot.service';

@Component({
  selector: 'app-screenshot-popover',
  templateUrl: './screenshot-popover.component.html',
  styleUrls: ['./screenshot-popover.component.scss'],
  standalone: true,
  imports: [CommonModule, PreviewImageComponent]
})
export class ScreenshotPopoverComponent implements OnDestroy {
  private defaultImage = `${ConfigService.environment.STATIC_FILES_URL}/icons/no_diagram_image.svg`;
  private defaultSize: string = SCREENSHOT_SIZE.SMALL;

  private configSub: Subject<ScreenshotConfig> = new Subject();
  private currentScreenshot: ScreenshotDataCell;

  public src: string;
  public altSrc: string;
  public sizeClass = new BehaviorSubject('screenshot-tooltip-small');

  constructor(
    private svc: ScreenshotService,
    private zone: NgZone
  ) {
    let altSrc: string;

    this.configSub
      .pipe(
        first(config => !!config),
        map(config => {
          if (!config.size) {
            config.size = this.defaultSize;
          }

          if (!config.defaultImage) {
            config.defaultImage = this.defaultImage;
          }

          return config;
        }),
        mergeMap(config => {
          altSrc = config.defaultImage;

          this.setSizeClass(config.size);
          const diagram = config.diagramId || config.id;

          return this.svc.getScreenshot(diagram, config.state, config.size);
        }),
        catchError(() => {
          this.currentScreenshot = null;
          this.zone.run(() => {
            //fixes bug with update happening between change detection cycles
            this.src = altSrc;
            this.altSrc = altSrc;
          });

          return EMPTY;
        })
      )
      .subscribe(data => {
        this.currentScreenshot = data;
        this.zone.run(() => {
          //fixes bug with update happening between change detection cycles
          this.src = data.url;
          this.altSrc = altSrc;
        });
      });
  }

  ngOnDestroy() {
    this.configSub.complete();
  }

  public injectConfig(config: ScreenshotConfig) {
    this.configSub.next(config);
  }

  public onError(err: any) {
    console.warn(err);

    if (this.currentScreenshot && typeof this.currentScreenshot.errorLoading === 'function') {
      this.currentScreenshot.errorLoading(err);
    }
  }

  private setSizeClass(size) {
    switch (size) {
      case SCREENSHOT_SIZE.SMALL:
        this.sizeClass.next('screenshot-tooltip-small');
        break;

      case SCREENSHOT_SIZE.MEDIUM:
        this.sizeClass.next('screenshot-tooltip-medium');
        break;

      case SCREENSHOT_SIZE.LARGE:
        this.sizeClass.next('screenshot-tooltip-large');
        break;

      default:
        this.sizeClass.next('screenshot-tooltip-small');
        break;
    }
  }
}
